<script setup lang="ts">
import type { NavigationMenuItem } from "@nuxt/ui";
import { IconsToolbarArticles, IconsToolbarCustomerService, IconsToolbarEvents, IconsToolbarLaptop, IconsToolbarPodcasts } from "#components";

const cart = useCartStore();

const auth = useAuthorizeStore();

const topNavItems = computed<NavigationMenuItem[]>(() => [
  {
    "aria-label": "Min profil",
    "icon": "fas:face-smile",
    "to": auth.loggedIn
      ? {
          name: "minside",
        }
      : {
          name: "login",
          query: {
            redirectTo: "/minside",
          },
        },
  },
  {
    "aria-label": cart.isEmpty ? "handlekurv" : `handlekurv, det finnes ${cart.count} produkter i handlekurven din`,
    "icon": "fas:bag-shopping",
    "to": {
      name: "handlekurv",
    },
    "badge": cart.count,
  },
]);

const sidebarItems = computed<NavigationMenuItem[]>(() => [{
  label: "Produktkatalog",
  to: { name: "products" },
  icon: IconsToolbarLaptop,
}, {
  label: "Arrangementer",
  to: { name: "events" },
  icon: IconsToolbarEvents,
}, {
  label: "Artikler",
  to: { name: "articles" },
  icon: IconsToolbarArticles,
}, {
  label: "Podkaster",
  icon: IconsToolbarPodcasts,
  to: { name: "podcasts" },
}, {
  label: "Kundeservice",
  to: { name: "kundeservice" },
  icon: IconsToolbarCustomerService,
}, {
  label: "Om Aschehoug",
  to: { name: "om-aschehoug" },
}]);

const q = ref<string>();

function handleSearch() {
  if (q.value?.length) {
    navigateTo({ name: "search", query: { q: q.value } });
  }
}
</script>

<template>
  <UHeader
    title="Gå til forsiden"
    mode="slideover"
    :ui="{
      overlay: 'lg:block bg-[#D15C5C]/80',
      toggle: 'lg:block',
      content: 'lg:block bg-green-50 ring-transparent',
      body: 'px-s py-m xs:p-m sm:pl-xl',
      header: 'border-green-pop [&_nav]:hidden [&_a]:hidden [&_button]:text-green-30',
    }"
    :toggle="{
      class: 'text-white hover:text-current hover:contrast-75',
      variant: 'link',
      ui: {
        leadingIcon: 'size-[1.85rem] xs:size-9.25',
      },
    }"
  >
    <template #title>
      <IconsToolbarLogo fill="white" class="w-36 xs:w-auto" />
    </template>

    <template #right>
      <nav class="mr-4 flex items-center gap-6 text-white xs:mr-8 xs:gap-10">
        <NuxtLink
          v-for="item in topNavItems"
          :key="item['aria-label']"
          class="relative"
          :to="item.to"
        >
          <UIcon
            v-if="item.icon"
            :name="item.icon"
            class="size-6 xs:size-7"
          />

          <UBadge
            v-if="item.badge"
            :label="String(item.badge)"
            class="absolute right-3 -bottom-3 flex size-5 items-center justify-center rounded-full bg-yellow-40 text-xs font-semibold text-black-80 xs:right-4 xs:-bottom-4 xs:size-7"
          />
        </NuxtLink>
      </nav>
    </template>

    <template #body>
      <div class="flex flex-col border-b border-green-30">
        <label for="searchfield" class="text-base-versal text-green-30 xs:text-lg-versal">SØK</label>
        <UiSearchField v-model="q" @submit.prevent="handleSearch" />
      </div>

      <UNavigationMenu
        :items="sidebarItems"
        orientation="vertical"
        :ui="{
          linkLeadingIcon: 'size-8',
          link: 'before:hidden text-white no-underline hover:text-yellow-40 text-2xl xs:text-sidebar-link gap-x-7 py-6 border-b border-green-30',
        }"
      />
    </template>
  </UHeader>
</template>
